import { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { tokenStorage } from 'utils/storage';
import { authService } from 'services/auth.service';
import { ROUTES } from 'constants/index';
import { isRegistrationPage } from 'utils/publicApi';
import { useToast } from './useToast';


// Global flag to ensure token expired message only shows once across all instances
let globalTokenExpiredShown = false;

/**
 * Hook to monitor JWT token expiration and redirect to login when expired
 * Checks the token expiration every 30 seconds
 */
export const useTokenExpiration = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const hasRedirected = useRef(false);

  useEffect(() => {
    // Check token expiration immediately
    const checkTokenExpiration = () => {
      if (location.pathname === ROUTES.LOGIN || isRegistrationPage()) {
        globalTokenExpiredShown = false;
        return;
      }

      if (tokenStorage.isRecentlyAuthenticated()) {
        return;
      }

      const isManualLogout = sessionStorage.getItem('__manual_logout__') === 'true';
      if (isManualLogout) {
        sessionStorage.removeItem('__manual_logout__');
        return;
      }

      const token = tokenStorage.getAccessToken();
      
      if (!token) {
        // No token, but don't redirect if already redirected or message already shown globally
        if (!hasRedirected.current && !globalTokenExpiredShown) {
          hasRedirected.current = true;
          globalTokenExpiredShown = true;
          toast.error('Token expired. You will be logged out.', 'Token Expired');
          authService.clearSession();
          navigate(ROUTES.LOGIN, { replace: true });
        }
        return;
      }

      // Check if token is expired
      if (authService.isTokenExpired(token)) {
        if (!hasRedirected.current && !globalTokenExpiredShown) {
          hasRedirected.current = true;
          globalTokenExpiredShown = true;
          toast.error('Token expired. You will be logged out.', 'Token Expired');
          authService.clearSession();
          navigate(ROUTES.LOGIN, { replace: true });
        }
        return;
      }

      // Check if token will expire within 1 minute
      if (authService.willExpireWithin(token, 60)) {
        toast.warning('Your session is about to expire. Please save your work.', 'Session Warning');
      }
    };

    // Run immediately
    checkTokenExpiration();

    // Set up interval to check every 30 seconds
    intervalRef.current = setInterval(checkTokenExpiration, 30000);

    // Reset the redirected flag when component unmounts or user navigates
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      hasRedirected.current = false;
    };
  }, [navigate, toast, location.pathname]);

  useEffect(() => {
    // Reset the redirect flag when navigating to a protected route
    if (location.pathname !== ROUTES.LOGIN && !isRegistrationPage()) {
      hasRedirected.current = false;
    } else {
      // Reset global flag when navigating to login/register
      globalTokenExpiredShown = false;
    }
  }, [location.pathname]);
};

